import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Link,
  Grid,
  TablePagination,
  TableSortLabel
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { Link as RouterLink } from "react-router-dom";
import { getAllCustomersWithClients } from "../../../../../../dao/api";
import AdminTable from "../AdminTable";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: 0,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  filtersContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: "#F8F9FA",
    borderRadius: "8px",
    border: "1px solid #E1E6F1",
  },
  title: {
    fontSize: "24px",
    fontWeight: 600,
    color: "#1A1E24",
  },
  searchField: {
    minWidth: 200,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      "& fieldset": { borderColor: "#E1E6F1" },
      "&:hover fieldset": { borderColor: "#54606F" },
      "&.Mui-focused fieldset": { borderColor: "#54606F" },
    },
  },
  customerRow: {
    backgroundColor: "#F8F9FA",
    "& .MuiTableCell-root": {
      fontWeight: 600,
      borderBottom: "2px solid #E1E6F1",
    },
  },
  clientRow: {
    "& .MuiTableCell-root": {
      borderBottom: "1px solid #E1E6F1",
    },
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
  noData: {
    textAlign: "center",
    color: "#54606F",
    padding: theme.spacing(4),
    fontSize: "16px",
  },
  expandButton: {
    padding: theme.spacing(0.5),
  },
  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  link: {
    color: "#5195E6",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const AllClientsTab = () => {
  const classes = useStyles();

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [expandedCustomers, setExpandedCustomers] = useState(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setLoading(true);
        const data = await getAllCustomersWithClients();
        setCustomers(data || []);
      } catch (error) {
        console.error("Error loading customers:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCustomers();
  }, []);

  const toggleCustomerExpansion = (customerId) => {
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(customerId)) {
      newExpanded.delete(customerId);
    } else {
      newExpanded.add(customerId);
    }
    setExpandedCustomers(newExpanded);
  };

  const filterCustomersAndClients = (customers, searchTerm) => {
    if (!searchTerm) return customers;
    
    const lowercaseSearch = searchTerm.toLowerCase();
    
    return customers.filter(customer => {
      // Check if customer matches
      const customerMatches = 
        customer.name?.toLowerCase().includes(lowercaseSearch) ||
        customer.externalId?.toLowerCase().includes(lowercaseSearch);
      
      // Check if any client matches
      const clientMatches = customer.clients?.some(client =>
        client.name?.toLowerCase().includes(lowercaseSearch) ||
        client.externalId?.toLowerCase().includes(lowercaseSearch)
      );
      
      return customerMatches || clientMatches;
    }).map(customer => {
      // If customer doesn't match but has matching clients, show only matching clients
      const customerMatches = 
        customer.name?.toLowerCase().includes(lowercaseSearch) ||
        customer.externalId?.toLowerCase().includes(lowercaseSearch);
      
      if (customerMatches) {
        return customer; // Show all clients if customer matches
      } else {
        // Show only matching clients
        return {
          ...customer,
          clients: customer.clients?.filter(client =>
            client.name?.toLowerCase().includes(lowercaseSearch) ||
            client.externalId?.toLowerCase().includes(lowercaseSearch)
          ) || []
        };
      }
    });
  };

  const filteredCustomers = filterCustomersAndClients(customers, searchTerm);

  // Sorting
  const sortedCustomers = React.useMemo(() => {
    if (!orderBy) return filteredCustomers;

    const sorted = [...filteredCustomers].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'externalId':
          aValue = a.externalId || '';
          bValue = b.externalId || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [filteredCustomers, orderBy, order]);
  
  // Pagination
  const paginatedCustomers = sortedCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box className={classes.container}>

      {/* Filters */}
      <Box className={classes.filtersContainer}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              className={classes.searchField}
              placeholder="Поиск по названию или ИНН..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            {sortedCustomers.length > 0 && (
              <Box style={{ display: "flex", justifyContent: "flex-end" }}>
                <TablePagination
                  component="div"
                  count={sortedCustomers.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 50, 100, 200]}
                  labelRowsPerPage="Строк на странице:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} из ${count !== -1 ? count : `более чем ${to}`}`
                  }
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <AdminTable>
        <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Название
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={orderBy === 'externalId'}
                  direction={orderBy === 'externalId' ? order : 'asc'}
                  onClick={() => handleRequestSort('externalId')}
                >
                  ИНН
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCustomers.length > 0 ? (
              paginatedCustomers.map((customer) => (
                <React.Fragment key={customer.id}>
                  {/* Customer Row */}
                  <TableRow className={classes.customerRow}>
                    <TableCell className={classes.tableCell}>
                      <Box className={classes.nameCell}>
                        <IconButton
                          className={classes.expandButton}
                          onClick={() => customer.clients && customer.clients.length > 0 && toggleCustomerExpansion(customer.id)}
                          disabled={!customer.clients || customer.clients.length === 0}
                          style={{ 
                            opacity: (!customer.clients || customer.clients.length === 0) ? 0.3 : 1 
                          }}
                        >
                          {customer.clients && customer.clients.length > 0 && expandedCustomers.has(customer.id) ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                        <Link
                          component={RouterLink}
                          to={`/customer/details/${customer.id}`}
                          className={classes.link}
                        >
                          {customer.name || "-"}
                        </Link>
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {customer.externalId || "-"}
                    </TableCell>
                  </TableRow>
                  
                  {/* Client Rows */}
                  {customer.clients && customer.clients.length > 0 && 
                    customer.clients.map((client) => (
                      <TableRow key={client.id} className={classes.clientRow}>
                        <Collapse 
                          component={TableCell}
                          in={expandedCustomers.has(customer.id)}
                          timeout="auto"
                          unmountOnExit
                          className={classes.tableCell}
                        >
                          <Box style={{ paddingLeft: "48px" }}>
                            <Link
                              component={RouterLink}
                              to={`/client/details/${client.id}`}
                              className={classes.link}
                            >
                              {client.name || "-"}
                            </Link>
                          </Box>
                        </Collapse>
                        <Collapse 
                          component={TableCell}
                          in={expandedCustomers.has(customer.id)}
                          timeout="auto"
                          unmountOnExit
                          className={classes.tableCell}
                        >
                          {client.externalId || "-"}
                        </Collapse>
                      </TableRow>
                    ))
                  }
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} className={classes.noData}>
                  {searchTerm ? "Клиенты не найдены" : "Нет данных о клиентах"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
      </AdminTable>
    </Box>
  );
};

export default AllClientsTab;